<template>
  <div
    ref="containerRef"
    class="vehicle-perception-canvas w-full h-full min-h-[240px] block touch-none"
  />
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";
import { DEFAULT_PREVIEW_SCENE_DISPLAY_RANGE_M } from "@/constants/previewSceneDisplay";
import { createVehiclePerceptionScene } from "./vehiclePerceptionScene";
import type { ObstacleInfo } from "./vehiclePerceptionTypes";

const props = withDefaults(
  defineProps<{
    obstacles?: ObstacleInfo[];
    vehicleSpeedKmh?: number;
    previewDisplayRangeM?: number;
  }>(),
  {
    obstacles: () => [],
    vehicleSpeedKmh: 0,
    previewDisplayRangeM: DEFAULT_PREVIEW_SCENE_DISPLAY_RANGE_M,
  }
);

const containerRef = ref<HTMLElement | null>(null);
let api: ReturnType<typeof createVehiclePerceptionScene> | null = null;
let ro: ResizeObserver | null = null;

function mountScene (): void {
  const el = containerRef.value;
  if (!el) {
    return;
  }
  api?.dispose();
  api = createVehiclePerceptionScene({
    container: el,
    getObstacles: () => props.obstacles,
    getVehicleSpeedKmh: () => props.vehicleSpeedKmh ?? 0,
    getPreviewDisplayRangeM: () => props.previewDisplayRangeM ?? DEFAULT_PREVIEW_SCENE_DISPLAY_RANGE_M,
  });
}

function resetView (): void {
  api?.resetView();
}

defineExpose({ resetView });

onMounted(() => {
  mountScene();
  const el = containerRef.value;
  if (!el) {
    return;
  }
  ro = new ResizeObserver(() => {
    api?.resize();
  });
  ro.observe(el);
});

onBeforeUnmount(() => {
  ro?.disconnect();
  ro = null;
  api?.dispose();
  api = null;
});
</script>
